import React, { useEffect, useState } from "react";
import fetchData from "../../api/fetchApi";
import { useParams, useNavigate } from "react-router-dom";
import { 
  User, Mail, Phone, Calendar, Shield, CreditCard, 
  Activity, Clock, Lock, Gift, RefreshCw, ChevronRight,
  Wallet, History, CheckCircle, XCircle, ArrowUpRight, ArrowDownLeft,
  ArrowLeft, Globe
} from "lucide-react";

// Modals copied from MembersPage.js
function RedeemVoucherModal({ open, onClose, memberId, onSuccess }) {
  const [voucherCode, setVoucherCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await fetchData(`chaat/members/${memberId}/redeem`, "POST", {
        voucher_code: voucherCode
      });
      setSuccess("تم تفعيل القسيمة بنجاح");
      onSuccess && onSuccess();
      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1200);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء تفعيل القسيمة");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <h3 className="font-bold text-lg mb-4">تفعيل قسيمة</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">رمز القسيمة
            <input type="text" value={voucherCode} onChange={e => setVoucherCode(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="رمز القسيمة" required />
          </label>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          {success && <div className="text-green-600 text-sm">{success}</div>}
          <div className="flex gap-2 mt-2">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded" disabled={loading}>{loading ? "جاري التفعيل..." : "تفعيل"}</button>
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={onClose}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChangePasswordModal({ open, onClose, memberId, onSuccess }) {
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await fetchData(`chaat/members/${memberId}/password`, "PUT", {
        password,
        password_confirmation: passwordConfirmation
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء تغيير كلمة المرور");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <h3 className="font-bold text-lg mb-4">تغيير كلمة المرور</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">كلمة المرور الجديدة
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="كلمة المرور الجديدة" required minLength={8} />
          </label>
          <label className="text-sm font-medium text-gray-700">تأكيد كلمة المرور
            <input type="password" value={passwordConfirmation} onChange={e => setPasswordConfirmation(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="تأكيد كلمة المرور" required minLength={8} />
          </label>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex gap-2 mt-2">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded" disabled={loading}>{loading ? "جاري التحديث..." : "تحديث"}</button>
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={onClose}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ExpiredDateModal({ open, onClose, memberId, onSuccess }) {
  const [days, setDays] = useState(1);
  const [amountPrice, setAmountPrice] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState("LYD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await fetchData(`chaat/members/${memberId}/expired_date`, "PUT", {
        days: Number(days),
        amount_price: Number(amountPrice),
        currency_symbol: currencySymbol
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء التحديث");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <h3 className="font-bold text-lg mb-4">تحديث تاريخ انتهاء العضوية</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">عدد الأيام
            <input type="number" min={1} value={days} onChange={e => setDays(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="عدد الأيام" required />
          </label>
          <label className="text-sm font-medium text-gray-700">المبلغ
            <input type="number" min={0} value={amountPrice} onChange={e => setAmountPrice(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="المبلغ" required />
          </label>
          <label className="text-sm font-medium text-gray-700">رمز العملة
            <select value={currencySymbol} onChange={e => setCurrencySymbol(e.target.value)} className="border rounded px-3 py-2 w-full mt-1">
              <option value="LYD">LYD</option>
              <option value="USD">USD</option>
            </select>
          </label>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex gap-2 mt-2">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded" disabled={loading}>{loading ? "جاري التحديث..." : "تحديث"}</button>
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={onClose}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChangePackageModal({ open, onClose, memberId, packages, onSuccess }) {
  const [packageId, setPackageId] = useState("");
  const [days, setDays] = useState(1);
  const [amountPrice, setAmountPrice] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState("LYD");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await fetchData(`chaat/members/${memberId}/package/${packageId}`, "PUT", {
        days: Number(days),
        amount_price: Number(amountPrice),
        currency_symbol: currencySymbol
      });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء التحديث");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <h3 className="font-bold text-lg mb-4">تغيير الباقة</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">الباقة الجديدة
            <select value={packageId} onChange={e => setPackageId(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" required>
              <option value="">اختر الباقة</option>
              {packages.map(pkg => (
                <option key={pkg.id} value={pkg.id}>{pkg.package_name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">عدد الأيام
            <input type="number" min={1} value={days} onChange={e => setDays(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="عدد الأيام" required />
          </label>
          <label className="text-sm font-medium text-gray-700">المبلغ
            <input type="number" min={0} value={amountPrice} onChange={e => setAmountPrice(e.target.value)} className="border rounded px-3 py-2 w-full mt-1" placeholder="المبلغ" required />
          </label>
          <label className="text-sm font-medium text-gray-700">رمز العملة
            <select value={currencySymbol} onChange={e => setCurrencySymbol(e.target.value)} className="border rounded px-3 py-2 w-full mt-1">
              <option value="LYD">LYD</option>
              <option value="USD">USD</option>
            </select>
          </label>
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <div className="flex gap-2 mt-2">
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded" disabled={loading}>{loading ? "جاري التحديث..." : "تحديث"}</button>
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={onClose}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ShowMemberPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [expiredModal, setExpiredModal] = useState({ open: false, memberId: null });
  const [changePackageModal, setChangePackageModal] = useState({ open: false, memberId: null });
  const [changePasswordModal, setChangePasswordModal] = useState({ open: false, memberId: null });
  const [redeemVoucherModal, setRedeemVoucherModal] = useState({ open: false, memberId: null });
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchMember = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchData(`chaat/members/${id}`);
        setMember(data.data);
      } catch (err) {
        setError(err.message || "حدث خطأ أثناء جلب البيانات");
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
    fetchData("chaat/packages").then(data => setPackages(data.data || []));
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );
  
  if (error) return (
    <div className="p-6 bg-red-50 text-red-600 rounded-lg mx-6 mt-6 border border-red-100 flex items-center gap-2">
      <XCircle size={20} />
      {error}
    </div>
  );

  if (!member) return null;

  const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
      status === "1" ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === "1" ? "bg-green-500" : "bg-red-500"}`}></span>
      {status === "1" ? "نشط" : "غير نشط"}
    </span>
  );

  const InfoItem = ({ icon: Icon, label, value, className = "" }) => (
    <div className={`flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors ${className}`}>
      <div className="p-2 bg-white border border-gray-100 rounded-lg shadow-sm text-gray-500">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-0.5">{label}</div>
        <div className="text-sm font-medium text-gray-900">{value || "-"}</div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 space-y-6 bg-gray-50 min-h-screen" dir="rtl">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-blue-400 to-indigo-400 relative">
          <button 
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg backdrop-blur-sm transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="px-6 pb-6 relative">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-end -mt-12 mb-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-white p-1 shadow-lg">
                <div className="w-full h-full rounded-xl bg-blue-50 flex items-center justify-center text-3xl font-bold text-blue-600 border border-blue-100">
                  {member.name?.charAt(0) || "M"}
                </div>
              </div>
              <div className="absolute -bottom-2 -left-2">
                <StatusBadge status={member.status} />
              </div>
            </div>
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{member.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="opacity-50">#</span>{member.id}
                </span>
                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {member.user_type}
                </span>
                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  آخر دخول: {member.last_login_at || 'لم يسجل دخول'}
                </span>
              </div>
            </div>

            <div className="flex gap-2 w-full md:w-auto">
              <button 
                onClick={() => setExpiredModal({ open: true, memberId: member.id })}
                className="flex-1 md:flex-none items-center justify-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm inline-flex"
              >
                <Calendar size={16} />
                تمديد الاشتراك
              </button>
              <button 
                onClick={() => setChangePackageModal({ open: true, memberId: member.id })}
                className="flex-1 md:flex-none items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm inline-flex"
              >
                <Shield size={16} />
                تغيير الباقة
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-8 border-t border-gray-100 pt-6">
            <div className="flex gap-4">
               <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                 <Wallet size={24} />
               </div>
               <div>
                 <p className="text-sm text-gray-500 mb-0.5">إجمالي الرصيد</p>
                 <p className="text-lg font-bold text-gray-900">
                   {member.accounts?.[0]?.balance || "0.00"} 
                   <span className="text-xs font-normal text-gray-500 mr-1">{member.accounts?.[0]?.currency?.symbol || "LYD"}</span>
                 </p>
               </div>
            </div>
            
            <div className="flex gap-4">
               <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                 <Shield size={24} />
               </div>
               <div>
                 <p className="text-sm text-gray-500 mb-0.5">الباقة الحالية</p>
                 <p className="text-lg font-bold text-gray-900">{member.package?.package_name || "بدون باقة"}</p>
               </div>
            </div>

            <div className="flex gap-4">
               <div className="p-2.5 bg-amber-50 text-amber-600 rounded-lg">
                 <Activity size={24} />
               </div>
               <div>
                 <p className="text-sm text-gray-500 mb-0.5">حالة الاشتراك</p>
                 <p className="text-lg font-bold text-gray-900 is-truncated" title={member.expired_date_at}>{member.expired_date_at || "-"}</p>
               </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User size={20} className="text-blue-500" />
              المعلومات الشخصية
            </h3>
            <div className="space-y-1">
              <InfoItem icon={Mail} label="البريد الإلكتروني" value={member.email} />
              <InfoItem icon={Phone} label="رقم الهاتف" value={member.mobile} />
              <InfoItem icon={Calendar} label="تاريخ الانضمام" value={member.add_date} />
              <InfoItem icon={Globe} label="IP العنوان" value={member.last_login_ip} />
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
               <button 
                onClick={() => setChangePasswordModal({ open: true, memberId: member.id })}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
               >
                 <span className="flex items-center gap-2 text-sm font-medium text-gray-700 group-hover:text-blue-700">
                   <Lock size={16} />
                   تغيير كلمة المرور
                 </span>
                 <ArrowLeft size={16} className="text-gray-400 group-hover:text-blue-500" />
               </button>
               
               <button 
                onClick={() => setRedeemVoucherModal({ open: true, memberId: member.id })}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all group"
               >
                 <span className="flex items-center gap-2 text-sm font-medium text-gray-700 group-hover:text-purple-700">
                   <Gift size={16} />
                   تفعيل قسيمة
                 </span>
                 <ArrowLeft size={16} className="text-gray-400 group-hover:text-purple-500" />
               </button>
            </div>
          </div>
        </div>

        {/* Right Column - Tabs & Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200 shadow-sm w-fit">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'overview' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              نظرة عامة والاشتراكات
            </button>
            <button
              onClick={() => setActiveTab('finance')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'finance' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              المحفظة والعمليات
            </button>
          </div>

          {activeTab === 'overview' && (
             <div className="space-y-6">
               {/* Package History */}
               <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                 <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                   <h3 className="font-bold text-gray-900 flex items-center gap-2">
                     <History size={20} className="text-blue-500" />
                     سجل الاشتراكات
                   </h3>
                 </div>
                 <div className="overflow-x-auto">
                   <table className="w-full text-sm text-right">
                     <thead className="bg-gray-50 text-gray-500">
                       <tr>
                         <th className="px-5 py-3 font-medium">الباقة</th>
                         <th className="px-5 py-3 font-medium">السعر</th>
                         <th className="px-5 py-3 font-medium">تاريخ البدء</th>
                         <th className="px-5 py-3 font-medium">تاريخ الانتهاء</th>
                         <th className="px-5 py-3 font-medium">المدة</th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-100">
                       {member.package_history && member.package_history.length > 0 ? (
                         member.package_history.map((hist) => (
                           <tr key={hist.id} className="hover:bg-gray-50 transition-colors">
                             <td className="px-5 py-4 font-medium text-gray-900">{hist.package?.package_name}</td>
                             <td className="px-5 py-4 text-gray-600">{Number(hist.payed_price).toLocaleString()} {hist.package?.currency_symbol?.symbol}</td>
                             <td className="px-5 py-4 text-gray-600">{hist.start_date}</td>
                             <td className="px-5 py-4 text-gray-600">
                               <span className={`px-2 py-1 rounded text-xs ${new Date(hist.end_date) > new Date() ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                 {hist.end_date}
                               </span>
                             </td>
                             <td className="px-5 py-4 text-gray-600">{hist.days} يوم</td>
                           </tr>
                         ))
                       ) : (
                         <tr>
                           <td colSpan="5" className="px-5 py-8 text-center text-gray-500">
                             عفواً، لا يوجد سجل اشتراكات سابق
                           </td>
                         </tr>
                       )}
                     </tbody>
                   </table>
                 </div>
               </div>
             </div>
          )}

          {activeTab === 'finance' && (
             <div className="space-y-6">
                {/* Account Cards */}
                {member.accounts?.map((acc) => (
                  <div key={acc.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                         <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                           <Wallet size={20} />
                         </div>
                         <div>
                           <h4 className="font-bold text-gray-900">{acc.description || "الحساب الرئيسي"}</h4>
                           <span className="text-xs text-gray-500">ID: {acc.id}</span>
                         </div>
                      </div>
                      <div className="text-left">
                        <div className="text-sm text-gray-500">الرصيد الحالي</div>
                        <div className="text-xl font-bold text-gray-900">{acc.balance} <span className="text-sm font-normal">{acc.currency?.symbol}</span></div>
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <h5 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <ArrowUpRight size={16} className="text-gray-400" />
                        آخر المعاملات
                      </h5>
                      <div className="space-y-3">
                        {acc.transactions && acc.transactions.length > 0 ? (
                          acc.transactions.slice(0, 5).map((trx) => (
                            <div key={trx.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                  trx.type === 'deposit' ? 'bg-green-100 text-green-600' : 
                                  trx.type === 'transfer_out' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {trx.type === 'deposit' ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{trx.description}</div>
                                  <div className="text-xs text-gray-500">{trx.created_at}</div>
                                </div>
                              </div>
                              <div className={`font-bold text-sm ${
                                parseFloat(trx.amount) > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {parseFloat(trx.amount) > 0 ? '+' : ''}{trx.amount}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center text-gray-500 py-4">لا توجد معاملات حديثة</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
             </div>
          )}
        </div>
      </div>
      {/* Modals */}
      <ExpiredDateModal
        open={expiredModal.open}
        memberId={expiredModal.memberId}
        onClose={() => setExpiredModal({ open: false, memberId: null })}
        onSuccess={() => {}}
      />
      <ChangePackageModal
        open={changePackageModal.open}
        memberId={changePackageModal.memberId}
        packages={packages}
        onClose={() => setChangePackageModal({ open: false, memberId: null })}
        onSuccess={() => {}}
      />
      <ChangePasswordModal
        open={changePasswordModal.open}
        memberId={changePasswordModal.memberId}
        onClose={() => setChangePasswordModal({ open: false, memberId: null })}
        onSuccess={() => {}}
      />
      <RedeemVoucherModal
        open={redeemVoucherModal.open}
        memberId={redeemVoucherModal.memberId}
        onClose={() => setRedeemVoucherModal({ open: false, memberId: null })}
        onSuccess={() => {}}
      />
    </div>
  );
}
